import type { ReactNode } from "react";
import { Button } from "@kalodata/kds-design";
import { IconLineGrowth, IconLineLightning, IconLinePlus, IconSolidAI } from "@kalodata/kds-design-icons";
import { PostVideoButton } from "@components/PostVideoButton/PostVideoButton";
import type { LinkedAccount } from "@store/linkedAccountSlice";
import { useLinkAccount } from "@pages/me/hooks/useLinkAccount";
import { Trans } from "react-i18next";
import { useIsCreditActivityEligible } from "@store/globalSlice";
import { NationalFlag } from "@components/NationalFlag";
import { getRegionIcon } from "@utils/format";
import { useAppSelector } from "@hooks/redux";
import { SOCIAL_PUBLISHER_CREDITS_DAILY_CAP, SOCIAL_PUBLISHER_CREDITS_PER_VIDEO } from "./constants";

const VerifyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.4963 7.68455C12.7404 7.44047 13.1373 7.44047 13.3813 7.68455C13.6248 7.92857 13.625 8.32441 13.3813 8.56834L9.79004 12.1596C9.42846 12.5212 8.83838 12.5093 8.49121 12.134L6.61621 10.1028C6.38208 9.84913 6.39797 9.4531 6.65161 9.21898C6.90517 8.98529 7.30013 9.00114 7.53418 9.25438L9.1626 11.0183L12.4963 7.68455Z"
      fill="#70B9FF"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9.52637 2.06443C9.84218 1.92655 10.2011 1.92366 10.5188 2.05711C13.1388 3.15933 15.2572 3.97838 16.8726 4.10423C17.2274 4.13182 17.5 4.4007 17.5 4.72191V10.602C17.5 10.6709 17.4938 10.7405 17.4817 10.8083C16.8656 14.2581 14.4911 16.6517 10.3577 17.9861L10.1807 18.0276C10.0611 18.0464 9.93897 18.0473 9.81934 18.0288L9.64233 17.9861C5.58217 16.6882 3.22454 14.3999 2.57202 11.1208L2.51465 10.7998L2.5 10.6118V4.73411C2.5 4.40834 2.7805 4.13826 3.14087 4.11644C4.60313 4.02805 6.41566 3.38866 8.57788 2.47337L9.52637 2.06443ZM10.0269 3.21067C7.71015 4.22311 5.57048 5.07589 3.75 5.31028V10.6057C4.21469 13.4556 6.1359 15.5466 9.99756 16.7873C13.7978 15.5544 15.7354 13.4646 16.25 10.5947V5.28953C14.4894 5.03366 12.3656 4.19029 10.0342 3.20945C10.0342 3.20945 10.0299 3.20935 10.0269 3.21067Z"
      fill="#70B9FF"
    />
  </svg>
);

export interface PublishedEmptyStateProps {
  accounts: LinkedAccount[];
  onPostVideo: () => void;
  title?: ReactNode;
}

export const PublishedEmptyState = ({ accounts, onPostVideo, title }: PublishedEmptyStateProps) => {
  const linkAccount = useLinkAccount();
  const { isCreditActivityEligible } = useIsCreditActivityEligible();
  const usFlagUrl = useAppSelector((state) => getRegionIcon(state.configReducer.regionOptions, "US"));
  return (
    <div className="pt-[120px] px-[24px]">
      <div className="flex-1 min-w-0 flex flex-col items-center gap-[24px]">
        <div className="flex flex-col items-center gap-[8px]">
          <p className="text-center text-[20px] font-semibold leading-[24px] text-kds-fg">
            {title ?? <Trans>No Videos Posted Yet</Trans>}
          </p>
          {isCreditActivityEligible && (
            <p className="text-center text-[14px] text-kds-fg-soft flex items-center gap-[4px]">
              <Trans
                defaults="<usflag/> US Local and Cross-border Shops earn <coin/> <b>{{points}} Credits</b> per video posted via Kaloclip API (Daily cap: <coin/> <b>{{max}} Credits</b>)"
                values={{
                  points: SOCIAL_PUBLISHER_CREDITS_PER_VIDEO,
                  max: SOCIAL_PUBLISHER_CREDITS_DAILY_CAP,
                }}
                components={{
                  usflag: <NationalFlag url={usFlagUrl} className="h-[14px] w-[18.667px] shrink-0 border-0" />,
                  coin: <IconSolidAI size={16} className="inline shrink-0 text-kds-chart-amber" />,
                  b: <span className="font-medium text-kds-fg" />,
                }}
              />
            </p>
          )}
        </div>

        <div className="flex w-full items-center gap-[16px] rounded-[12px] border border-kds-border bg-kds-bg p-[12px]">
          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex items-center gap-[4px]">
              <VerifyIcon />
              <span className="text-[14px] font-medium leading-[16px] text-kds-fg">
                <Trans>Official API Authorization</Trans>
              </span>
            </div>
            <p className="h-[32px] line-clamp-2 text-[12px] leading-[16px] text-kds-fg-soft">
              <Trans>Secure publishing with no IP concerns and zero account ban risk</Trans>
            </p>
          </div>

          <div className="h-[56px] w-px shrink-0 bg-kds-border" />

          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex items-center gap-[4px]">
              <IconLineLightning size={20} className="shrink-0 text-kds-chart-violet-solid" />
              <span className="text-[14px] font-medium leading-[16px] text-kds-fg">
                <Trans>Streamlined Operations</Trans>
              </span>
            </div>
            <p className="h-[32px] line-clamp-2 text-[12px] leading-[16px] text-kds-fg-soft">
              <Trans>
                Post to multiple accounts and videos in one click, with scheduled publishing to lock in the best posting
                time
              </Trans>
            </p>
          </div>

          <div className="h-[56px] w-px shrink-0 bg-kds-border" />

          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex items-center gap-[4px]">
              <IconLineGrowth size={20} className="shrink-0 text-kds-success-fg" />
              <span className="text-[14px] font-medium leading-[16px] text-kds-fg">
                <Trans>Official Traffic Boost</Trans>
              </span>
            </div>
            <p className="h-[32px] line-clamp-2 text-[12px] leading-[16px] text-kds-fg-soft">
              <Trans>
                Publishing via the official API gives quality content a chance to receive platform traffic support
              </Trans>
            </p>
          </div>
        </div>

        <div className="flex gap-[12px]">
          {accounts.length === 0 && (
            <Button
              variant="outline"
              size="lg"
              iconPlacement="start"
              icon={<IconLinePlus size={20} />}
              onClick={() => void linkAccount()}
              loading={linkAccount.isPending()}
            >
              <Trans>Connect Account</Trans>
            </Button>
          )}
          <PostVideoButton variant="reverse" onClick={onPostVideo} />
        </div>
      </div>
    </div>
  );
};
